import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "../components/Header";
import { Shield, ChevronLeft, ChevronRight, BookOpen, Swords, Lightbulb, Check, X, ArrowRight, Eye, Pencil, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `/api`;

export default function BoucliersPage() {
  const [tab, setTab] = useState('fiches');
  const [fiches, setFiches] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [selectedFiche, setSelectedFiche] = useState(null);

  useEffect(() => {
    axios.get(`${API}/boucliers/fiches`).then(r => setFiches(r.data));
    axios.get(`${API}/boucliers/quiz`).then(r => setQuiz(r.data));
  }, []);

  const tabs = [
    { id: 'fiches', label: 'Les 5 situations', icon: BookOpen },
    { id: 'exercices', label: "S'entraîner", icon: Pencil },
    { id: 'quiz', label: 'Quiz', icon: Swords },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/dashboard" className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200"><ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour</Link>
        
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-[#6B8576] to-[#4A6B57] rounded-2xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }} data-testid="boucliers-title">Boucliers Paradoxaux</h1>
              <p className="text-[#6A7771] text-sm">On vous envoie un missile ? Renvoyez de l'amour.</p>
            </div>
          </div>
          
          <p className="text-[#5C6A61] text-sm leading-relaxed mt-4 mb-8 bg-white rounded-2xl border border-[#EBE7E0] p-5" style={{ fontFamily: 'Manrope' }}>
            Le principe : quelqu'un vous attaque avec une remarque blessante. Au lieu de contre-attaquer ou de vous écraser, vous renvoyez un compliment, de la chaleur, de la douceur. Résultat : l'attaquant est désarmé, son missile tombe dans le vide, et c'est vous qui gardez le pouvoir émotionnel.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setSelectedFiche(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${tab === t.id ? 'bg-[#2A3A31] text-white' : 'bg-white border border-[#EBE7E0] text-[#2C3531] hover:bg-[#EBE7E0]/40'}`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>

        {/* FICHES TAB */}
        {tab === 'fiches' && !selectedFiche && (
          <motion.div initial="hidden" animate="show" variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } }} className="space-y-3">
            {fiches.map((f, i) => (
              <motion.button key={f.id} variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
                onClick={() => setSelectedFiche(f)}
                className="w-full text-left bg-white rounded-3xl border border-[#EBE7E0] p-6 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group"
                data-testid={`fiche-${f.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#6B8576]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <span className="text-[#6B8576] font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                      <p className="text-sm text-[#6A7771] mt-0.5 italic">"{f.trigger}"</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#6A7771] group-hover:text-[#6B8576] group-hover:translate-x-1 transition-all duration-300" strokeWidth={1.5} />
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* FICHE DETAIL */}
        {tab === 'fiches' && selectedFiche && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
            <button onClick={() => setSelectedFiche(null)} className="text-sm text-[#6A7771] hover:text-[#6B8576] flex items-center gap-1 mb-6 transition-colors duration-200">
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Retour aux situations
            </button>
            <FicheDetail fiche={selectedFiche} />
          </motion.div>
        )}

        {/* EXERCICES TAB */}
        {tab === 'exercices' && <ExerciceSection />}

        {/* QUIZ TAB */}
        {tab === 'quiz' && quiz.length > 0 && <QuizSection questions={quiz} />}
      </main>
    </div>
  );
}

function FicheDetail({ fiche }) {
  return (
    <div className="space-y-6">
      <div className="bg-[#2A3A31] text-white rounded-3xl p-6 sm:p-8">
        <p className="text-white/50 text-xs uppercase tracking-wider mb-3" style={{ fontFamily: 'Manrope' }}>Le missile</p>
        <p className="text-xl font-medium mb-6" style={{ fontFamily: 'Outfit' }}>"{fiche.trigger}"</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white/10 rounded-2xl p-4">
            <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Objectif</p>
            <p className="text-sm text-white/90 leading-relaxed">{fiche.objective}</p>
          </div>
          <div className="bg-white/10 rounded-2xl p-4">
            <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Principe</p>
            <p className="text-sm text-white/90 leading-relaxed">{fiche.principle}</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-[#2C3531] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <Shield className="w-5 h-5 text-[#6B8576]" strokeWidth={1.5} /> Les boucliers
        </h3>
        <div className="space-y-4">
          {fiche.shields.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bg-white rounded-3xl border border-[#EBE7E0] p-5">
              <p className="text-xs uppercase tracking-wider text-[#6B8576] font-medium mb-3">{s.style}</p>
              <div className="space-y-2">
                {s.examples.map((ex, j) => (
                  <div key={j} className="bg-[#FAF9F6] rounded-2xl p-4 border-l-3 border-[#6B8576]" style={{ borderLeft: '3px solid #6B8576' }}>
                    <p className="text-[#2C3531] text-sm leading-relaxed italic">"{ex}"</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="bg-[#F0EBE3] rounded-2xl p-5">
        <p className="text-sm text-[#5C6A61] flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#D9A892]" strokeWidth={1.5} /> <strong>Rappel</strong> : on renvoie de l'amour, pas un missile. L'attaquant s'attend à une réaction. Le compliment le laisse sans voix.</p>
      </div>
    </div>
  );
}

function ExerciceSection() {
  const [step, setStep] = useState(0);
  const [remark, setRemark] = useState('');
  const [responses, setResponses] = useState({ flattery: '', link: '', humor: '' });
  const [visu, setVisu] = useState({ scene: '', body: '', emotion: '' });
  const [reflection, setReflection] = useState({ learned: '', emotion_choice: '', next_time: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/boucliers/exercise`, {
        exercise_type: 'personal_shield',
        responses: { remark, shields: responses, visualization: visu, reflection }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const steps = [
    { title: "Ton bouclier personnel", desc: "Choisis une remarque que tu as déjà reçue" },
    { title: "Formule tes 3 boucliers", desc: "3 styles de réponse paradoxale" },
    { title: "Visualisation", desc: "Ancre ta réponse dans ton corps" },
    { title: "Retour réflexif", desc: "Ce que tu as appris" }
  ];

  return (
    <div>
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <button onClick={() => setStep(i)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${i === step ? 'bg-[#6B8576] text-white' : i < step ? 'bg-[#6B8576]/20 text-[#6B8576]' : 'bg-[#EBE7E0] text-[#6A7771]'}`}
              data-testid={`exercise-step-${i}`}
            >{i + 1}</button>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 rounded transition-all duration-300 ${i < step ? 'bg-[#6B8576]/40' : 'bg-[#EBE7E0]'}`} />}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
          <h3 className="text-xl font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>{steps[step].title}</h3>
          <p className="text-sm text-[#6A7771] mb-6">{steps[step].desc}</p>

          {step === 0 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6">
              <label className="block text-sm font-medium text-[#2C3531] mb-2">La remarque que tu as reçue</label>
              <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={3} placeholder='Ex: "Tu devrais sourire plus souvent..."' value={remark} onChange={e => setRemark(e.target.value)} data-testid="exercise-remark" />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              {[
                { key: 'flattery', label: 'Flatterie douce', placeholder: 'Transforme le missile en compliment...' },
                { key: 'link', label: 'Lien complice', placeholder: 'Crée un lien de proximité avec l\'attaquant...' },
                { key: 'humor', label: 'Humour élégant', placeholder: 'Désamorce avec légèreté et classe...' }
              ].map(f => (
                <div key={f.key} className="bg-white rounded-3xl border border-[#EBE7E0] p-6">
                  <label className="block text-sm font-medium text-[#6B8576] mb-2 uppercase tracking-wider">{f.label}</label>
                  <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={2} placeholder={f.placeholder} value={responses[f.key]} onChange={e => setResponses(p => ({...p, [f.key]: e.target.value}))} data-testid={`exercise-${f.key}`} />
                </div>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 space-y-5">
              <div className="bg-[#2A3A31] text-white rounded-2xl p-5">
                <p className="text-white/70 text-sm leading-relaxed">
                  Ferme les yeux. Revois la scène : l'endroit, les gens, les mots. Visualise-toi ancré·e, calme, souriant·e. Prononce mentalement ton bouclier. Observe le ressenti.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-2">Comment ton corps réagit ?</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={2} value={visu.body} onChange={e => setVisu(p => ({...p, body: e.target.value}))} placeholder="Tensions, détente, chaleur..." data-testid="exercise-visu-body" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-2">Et ton cœur ? Ta respiration ?</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={2} value={visu.emotion} onChange={e => setVisu(p => ({...p, emotion: e.target.value}))} placeholder="Calme, puissant·e, libéré·e..." data-testid="exercise-visu-emotion" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="bg-white rounded-3xl border border-[#EBE7E0] p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-2">Ce que j'ai appris sur moi en formulant ces réponses</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={2} value={reflection.learned} onChange={e => setReflection(p => ({...p, learned: e.target.value}))} data-testid="exercise-reflection-learned" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-2">Quelle émotion je choisis d'activer maintenant dans ces situations</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={2} value={reflection.emotion_choice} onChange={e => setReflection(p => ({...p, emotion_choice: e.target.value}))} data-testid="exercise-reflection-emotion" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3531] mb-2">La prochaine fois, j'aimerais pouvoir...</label>
                <textarea className="w-full px-4 py-3 rounded-2xl border border-[#EBE7E0] bg-[#FAF9F6] focus:outline-none focus:ring-2 focus:ring-[#6B8576]/20 focus:border-[#6B8576] transition-all duration-300 resize-none" rows={2} value={reflection.next_time} onChange={e => setReflection(p => ({...p, next_time: e.target.value}))} data-testid="exercise-reflection-next" />
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button onClick={() => setStep(s => s - 1)} disabled={step === 0} className="px-5 py-2.5 text-sm font-medium text-[#6A7771] hover:text-[#2C3531] disabled:opacity-30 transition-colors duration-200 flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Précédent
            </button>
            {step < 3 ? (
              <button onClick={() => setStep(s => s + 1)} className="px-5 py-2.5 bg-[#6B8576] text-white rounded-full text-sm font-medium hover:bg-[#546A5E] transition-all duration-300 flex items-center gap-1" data-testid="exercise-next">
                Suivant <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            ) : (
              <button onClick={handleSave} disabled={saving} className="px-6 py-2.5 bg-[#6B8576] text-white rounded-full text-sm font-medium hover:bg-[#546A5E] transition-all duration-300 flex items-center gap-2 disabled:opacity-50" data-testid="exercise-save">
                {saved ? <><Check className="w-4 h-4" strokeWidth={2} /> Sauvegardé !</> : saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function QuizSection({ questions }) {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  const q = questions[current];

  const handleSelect = (idx) => {
    if (showFeedback) return;
    setSelected(idx);
    setShowFeedback(true);
    if (idx === q.correct) setScore(s => s + 1);
    setAnswers(a => [...a, { question_id: q.id, selected: idx, correct: q.correct }]);
  };

  const handleNext = () => {
    if (current < questions.length - 1) {
      setCurrent(c => c + 1);
      setSelected(null);
      setShowFeedback(false);
    } else {
      setFinished(true);
      axios.post(`${API}/boucliers/quiz-result`, { score: score + (selected === q?.correct ? 0 : 0), total: questions.length, answers }).catch(() => {});
    }
  };

  const handleRestart = () => {
    setCurrent(0);
    setSelected(null);
    setShowFeedback(false);
    setScore(0);
    setAnswers([]);
    setFinished(false);
  };

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
        <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${pct >= 80 ? 'bg-[#6B8576]/10' : pct >= 50 ? 'bg-[#D9A892]/10' : 'bg-red-50'}`}>
          <span className={`text-3xl font-bold ${pct >= 80 ? 'text-[#6B8576]' : pct >= 50 ? 'text-[#D9A892]' : 'text-[#B86666]'}`} style={{ fontFamily: 'Outfit' }}>{score}/{questions.length}</span>
        </div>
        <h3 className="text-2xl font-semibold text-[#2C3531] mb-2" style={{ fontFamily: 'Outfit' }} data-testid="quiz-result">
          {pct >= 80 ? 'Excellent ! Vous maîtrisez le bouclier paradoxal.' : pct >= 50 ? 'Pas mal ! Relisez les fiches et réessayez.' : 'Le missile vous a touché... Entraînez-vous !'}
        </h3>
        <p className="text-[#6A7771] text-sm mb-8">
          {pct >= 80 ? 'Vous renvoyez de l\'amour comme un pro !' : 'Rappelez-vous : on renvoie de l\'amour, jamais un missile.'}
        </p>
        <button onClick={handleRestart} className="px-6 py-3 bg-[#6B8576] text-white rounded-full font-medium text-sm hover:bg-[#546A5E] transition-all duration-300 inline-flex items-center gap-2" data-testid="quiz-restart">
          <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Recommencer
        </button>
      </motion.div>
    );
  }

  return (
    <div>
      {/* Progress */}
      <div className="flex items-center gap-1 mb-6">
        {questions.map((_, i) => (
          <div key={i} className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${i < current ? 'bg-[#6B8576]' : i === current ? 'bg-[#6B8576]/50' : 'bg-[#EBE7E0]'}`} />
        ))}
      </div>
      <p className="text-xs text-[#6A7771] mb-4">{current + 1} / {questions.length}</p>

      <AnimatePresence mode="wait">
        <motion.div key={current} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}>
          {/* Missile */}
          <div className="bg-[#2A3A31] text-white rounded-3xl p-6 mb-3">
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Le missile</p>
            <p className="text-lg font-medium" style={{ fontFamily: 'Outfit' }}>"{q.missile}"</p>
            <p className="text-white/50 text-sm mt-2">{q.context}</p>
          </div>

          <p className="text-sm font-medium text-[#2C3531] mb-3" style={{ fontFamily: 'Outfit' }}>Quelle est votre réponse ?</p>

          {/* Options */}
          <div className="space-y-2.5">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correct;
              const isSelected = i === selected;
              let style = 'bg-white border-[#EBE7E0] hover:border-[#6B8576]/30 hover:shadow-[0_4px_20px_rgba(44,53,49,0.06)] cursor-pointer';
              if (showFeedback) {
                if (isCorrect) style = 'bg-[#6B8576]/10 border-[#6B8576] ring-1 ring-[#6B8576]/20';
                else if (isSelected && !isCorrect) style = 'bg-red-50 border-[#B86666] ring-1 ring-[#B86666]/20';
                else style = 'bg-white border-[#EBE7E0] opacity-50';
              }
              return (
                <button key={i} onClick={() => handleSelect(i)} disabled={showFeedback}
                  className={`w-full text-left rounded-2xl border p-4 transition-all duration-300 ${style}`}
                  data-testid={`quiz-option-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all duration-300 ${showFeedback && isCorrect ? 'border-[#6B8576] bg-[#6B8576]' : showFeedback && isSelected ? 'border-[#B86666] bg-[#B86666]' : 'border-[#EBE7E0]'}`}>
                      {showFeedback && isCorrect && <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                      {showFeedback && isSelected && !isCorrect && <X className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-[#2C3531]">"{opt.text}"</p>
                      {showFeedback && (isSelected || isCorrect) && (
                        <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`text-xs mt-2 leading-relaxed ${isCorrect ? 'text-[#6B8576]' : 'text-[#B86666]'}`}>{opt.feedback}</motion.p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {showFeedback && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-end mt-6">
              <button onClick={handleNext} className="px-6 py-2.5 bg-[#6B8576] text-white rounded-full text-sm font-medium hover:bg-[#546A5E] transition-all duration-300 flex items-center gap-1" data-testid="quiz-next">
                {current < questions.length - 1 ? <>Suivant <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></> : 'Voir le résultat'}
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
